import React, { useState, useEffect, useRef } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    FormHelperText,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";
import {
    CANCELLATION_CATEGORY,
    DEFAULT_PAYMENT_DUE,
    PAYMENT_DUE_OPTIONS,
    PAYMENT_TYPE_OPTIONS,
    CATEGORY_OPTIONS,
    CANCELLATION_TYPE_OPTIONS,
    REFUND_METHOD_OPTIONS,
    STATUS_OPTIONS,
    getCancellationTypeLabel,
    hasValue,
} from 'utils/paymentTermConstants';

const getInitialFormData = () => ({
    name: '',
    payment_due: DEFAULT_PAYMENT_DUE,
    days: '',
    amount_percent: '',
    amount_actual: '',
    payment_type: '',
    refund_term: '',
    applicable_for_id: '',
    category: '',
    cancellation_type: '',
    refund_method: '',
    coupon_expiry_days: '',
    status: 'Draft'
});

// Fields that trigger name auto-generation
const AUTO_NAME_FIELDS = [
    'payment_type', 'payment_due', 'days', 'amount_percent', 'amount_actual',
    'category', 'cancellation_type'
];

// What we try to focus inside a field wrapper (text input, textarea or MUI Select combobox)
const FOCUSABLE_SELECTOR =
    'input:not([type="hidden"]):not([aria-hidden="true"]), textarea, [role="combobox"], [role="button"][aria-haspopup="listbox"]';

const NewPaymentTermModal = ({
                                 isOpen,
                                 onClose,
                                 onSave,
                             }) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState(getInitialFormData());
    const formRef = useRef(null);

    const [formErrors, setFormErrors] = useState({});
    const [accounts, setAccounts] = useState([]);
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    const isCancellation = formData.category === CANCELLATION_CATEGORY;
    const showCouponExpiry = isCancellation && formData.refund_method === 'Coupon';

    useEffect(() => {
        if (isOpen) {
            fetchAccounts();
            // Reset form when modal opens
            setFormData(getInitialFormData());
            setFormErrors({});
        }
    }, [isOpen]);

    const fetchAccounts = async () => {
        setLoadingAccounts(true);
        try {
            const response = await axiosInstance.get('/accounts/?dropdown=true');
            setAccounts(response.data || []);
        } catch (error) {
            console.error('Error fetching accounts:', error);
        } finally {
            setLoadingAccounts(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const updated = {
                ...prev,
                [name]: value
            };
            // Cancellation rules only support the "Days Prior to Departure" condition
            if (name === 'category' && value === CANCELLATION_CATEGORY) {
                updated.payment_due = DEFAULT_PAYMENT_DUE;
                updated.refund_term = '';
            }
            // Auto-generate name if relevant fields change, but only if name is empty or was auto-generated
            if (AUTO_NAME_FIELDS.includes(name) && (!prev.name || prev.name === generatePaymentTermName(prev))) {
                updated.name = generatePaymentTermName(updated);
            }
            return updated;
        });
        setFormErrors(prev => ({
            ...prev,
            [name]: '',
            // % and flat amount are validated together
            ...(name === 'amount_percent' || name === 'amount_actual'
                ? { amount_percent: '', amount_actual: '' }
                : {}),
            ...(name === 'refund_method' ? { coupon_expiry_days: '' } : {})
        }));
    };

    const generatePaymentTermName = (data) => {
        if (data.category === CANCELLATION_CATEGORY) {
            if (!data.cancellation_type) return '';
            const typeLabel = getCancellationTypeLabel(data.cancellation_type);
            return hasValue(data.days)
                ? `${typeLabel} Cancellation - ${data.days} Days`
                : `${typeLabel} Cancellation`;
        }
        if (!data.payment_type && !data.payment_due) return '';
        const parts = [];
        if (data.payment_type) parts.push(data.payment_type);
        if (data.payment_due) parts.push(data.payment_due);
        if (data.days) parts.push(`${data.days} days`);
        if (data.amount_percent) parts.push(`${data.amount_percent}%`);
        if (data.amount_actual) parts.push(`$${data.amount_actual}`);
        return parts.join(' - ') || '';
    };

    // Scrolls the first field (in on-screen order) that has an error into view and focuses it
    const focusFirstError = (errors) => {
        // wait a tick so the error state has rendered
        setTimeout(() => {
            const form = formRef.current;
            if (!form) return;
            const target = Array.from(form.querySelectorAll('[data-field]'))
                .find((el) => errors[el.dataset.field]);
            if (!target) return;
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const focusable = target.querySelector(FOCUSABLE_SELECTOR);
            if (focusable) focusable.focus({ preventScroll: true });
        }, 0);
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.category) errors.category = 'Category is required';
        if (!formData.payment_due) errors.payment_due = isCancellation ? 'Refund Condition is required' : 'Payment Due is required';
        if (!formData.status) errors.status = 'Status is required';

        if (isCancellation) {
            if (!formData.cancellation_type) errors.cancellation_type = 'Cancellation Type is required';
            if (!formData.refund_method) errors.refund_method = 'Refund Method is required';

            // Coupon expiry only applies to the Coupon refund method
            if (formData.refund_method === 'Coupon') {
                if (!hasValue(formData.coupon_expiry_days)) {
                    errors.coupon_expiry_days = 'Coupon Expiry (days) is required';
                } else if (!Number.isInteger(Number(formData.coupon_expiry_days)) || Number(formData.coupon_expiry_days) <= 0) {
                    errors.coupon_expiry_days = 'Enter a whole number of days greater than 0';
                }
            }

            if (!hasValue(formData.days)) {
                errors.days = 'Days is required';
            } else if (Number(formData.days) < 0) {
                errors.days = 'Days cannot be negative';
            }

            // Cancellation rules are percentage-based only (Amount and Applicable For are hidden)
            if (!hasValue(formData.amount_percent)) {
                errors.amount_percent = 'Amount (%) is required';
            } else {
                const pct = Number(formData.amount_percent);
                if (pct < 0 || pct > 100) errors.amount_percent = 'Percentage must be between 0 and 100';
            }
        } else if (!formData.payment_type) {
            errors.payment_type = 'Payment Type is required';
        }

        if (!formData.name || formData.name.trim() === '') {
            // Auto-generate name if not provided
            const generatedName = generatePaymentTermName(formData);
            if (generatedName) {
                setFormData(prev => ({ ...prev, name: generatedName }));
            } else {
                errors.name = 'Name is required';
            }
        }

        setFormErrors(errors);
        const isValid = Object.keys(errors).length === 0;
        if (!isValid) focusFirstError(errors);
        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            // Ensure name is set
            const finalName = formData.name.trim() || generatePaymentTermName(formData);

            const payload = {
                ...formData,
                name: finalName,
                payment_due: isCancellation ? DEFAULT_PAYMENT_DUE : formData.payment_due,
                payment_type: isCancellation ? null : formData.payment_type,
                days: hasValue(formData.days) ? parseInt(formData.days, 10) : null,
                amount_percent: hasValue(formData.amount_percent) ? parseFloat(formData.amount_percent) : null,
                amount_actual: !isCancellation && hasValue(formData.amount_actual) ? parseFloat(formData.amount_actual) : null,
                applicable_for_id: !isCancellation && formData.applicable_for_id ? parseInt(formData.applicable_for_id, 10) : null,
                ...(!isCancellation && { refund_term: formData.refund_term || null }),
                category: formData.category || null,
                cancellation_type: isCancellation ? formData.cancellation_type : null,
                refund_method: isCancellation ? formData.refund_method : null,
                coupon_expiry_days: showCouponExpiry && hasValue(formData.coupon_expiry_days)
                    ? parseInt(formData.coupon_expiry_days, 10)
                    : null,
                status: formData.status || 'Draft',
                created_by: user?.id,
                updated_by: user?.id
            };

            if (isCancellation) {
                delete payload.refund_term;
            }

            await onSave(payload);
            setFormData(getInitialFormData());
            onClose();
        } catch (error) {
            console.error('Error saving Payment Term:', error);
            const detail = error.response?.data?.detail;
            setToast({
                open: true,
                message: (typeof detail === 'string' && detail) || error.message || 'Error saving Payment Term',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '80%', md: '700px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Payment Term</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        {/* noValidate turns off the browser's native "Please fill out this field" tooltip;
                            validation is handled by validateForm() */}
                        <form onSubmit={handleSubmit} noValidate ref={formRef}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} data-field="name">
                                    <TextField
                                        fullWidth
                                        label="Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                        error={!!formErrors.name}
                                        helperText={formErrors.name || 'Auto-generated from payment details (can be edited)'}
                                        required
                                    />
                                </Grid>

                                {/* Category drives which fields are shown, so it comes first */}
                                <Grid item xs={12} sm={6} data-field="category">
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.category} required>
                                        <InputLabel>Category</InputLabel>
                                        <Select
                                            name="category"
                                            value={formData.category}
                                            onChange={handleInputChange}
                                            label="Category"
                                        >
                                            {CATEGORY_OPTIONS.map(opt => (
                                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.category && <FormHelperText>{formErrors.category}</FormHelperText>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6} data-field="status">
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.status} required>
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            label="Status"
                                        >
                                            {STATUS_OPTIONS.map(opt => (
                                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.status && <FormHelperText>{formErrors.status}</FormHelperText>}
                                    </FormControl>
                                </Grid>

                                {isCancellation && (
                                    <>
                                        <Grid item xs={12} sm={6} data-field="cancellation_type">
                                            <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.cancellation_type} required>
                                                <InputLabel>Cancellation Type</InputLabel>
                                                <Select
                                                    name="cancellation_type"
                                                    value={formData.cancellation_type}
                                                    onChange={handleInputChange}
                                                    label="Cancellation Type"
                                                >
                                                    {CANCELLATION_TYPE_OPTIONS.map(opt => (
                                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                                    ))}
                                                </Select>
                                                {formErrors.cancellation_type && <FormHelperText>{formErrors.cancellation_type}</FormHelperText>}
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12} sm={6} data-field="refund_method">
                                            <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.refund_method} required>
                                                <InputLabel>Refund Method</InputLabel>
                                                <Select
                                                    name="refund_method"
                                                    value={formData.refund_method}
                                                    onChange={handleInputChange}
                                                    label="Refund Method"
                                                >
                                                    {REFUND_METHOD_OPTIONS.map(opt => (
                                                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                                    ))}
                                                </Select>
                                                {formErrors.refund_method && <FormHelperText>{formErrors.refund_method}</FormHelperText>}
                                            </FormControl>
                                        </Grid>

                                        {showCouponExpiry && (
                                            <Grid item xs={12} sm={6} data-field="coupon_expiry_days">
                                                <TextField
                                                    fullWidth
                                                    label="Coupon Expiry (days)"
                                                    name="coupon_expiry_days"
                                                    type="number"
                                                    value={formData.coupon_expiry_days}
                                                    onChange={handleInputChange}
                                                    variant="outlined"
                                                    margin="normal"
                                                    required
                                                    error={!!formErrors.coupon_expiry_days}
                                                    helperText={formErrors.coupon_expiry_days || 'Days the cancellation coupon stays valid'}
                                                />
                                            </Grid>
                                        )}
                                    </>
                                )}

                                <Grid item xs={12} sm={6} data-field="payment_due">
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.payment_due} required>
                                        <InputLabel>{isCancellation ? 'Refund Condition' : 'Payment Due'}</InputLabel>
                                        <Select
                                            name="payment_due"
                                            value={formData.payment_due}
                                            onChange={handleInputChange}
                                            label={isCancellation ? 'Refund Condition' : 'Payment Due'}
                                        >
                                            {(isCancellation ? [DEFAULT_PAYMENT_DUE] : PAYMENT_DUE_OPTIONS).map(opt => (
                                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.payment_due && <FormHelperText>{formErrors.payment_due}</FormHelperText>}
                                    </FormControl>
                                </Grid>

                                {!isCancellation && (
                                    <Grid item xs={12} sm={6} data-field="payment_type">
                                        <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.payment_type} required>
                                            <InputLabel>Payment Type</InputLabel>
                                            <Select
                                                name="payment_type"
                                                value={formData.payment_type}
                                                onChange={handleInputChange}
                                                label="Payment Type"
                                            >
                                                {PAYMENT_TYPE_OPTIONS.map(opt => (
                                                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                                ))}
                                            </Select>
                                            {formErrors.payment_type && <FormHelperText>{formErrors.payment_type}</FormHelperText>}
                                        </FormControl>
                                    </Grid>
                                )}

                                <Grid item xs={12} sm={6} data-field="days">
                                    <TextField
                                        fullWidth
                                        label="Days"
                                        name="days"
                                        type="number"
                                        value={formData.days}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                        required={isCancellation}
                                        error={!!formErrors.days}
                                        helperText={formErrors.days || (isCancellation ? 'Rule applies from this many days before departure' : '')}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} data-field="amount_percent">
                                    <TextField
                                        fullWidth
                                        label="Amount (%)"
                                        name="amount_percent"
                                        type="number"
                                        value={formData.amount_percent}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                        required={isCancellation}
                                        error={!!formErrors.amount_percent}
                                        helperText={formErrors.amount_percent || (isCancellation ? 'Refund percentage applied to the amount paid' : '')}
                                    />
                                </Grid>

                                {!isCancellation && (
                                    <Grid item xs={12} sm={6} data-field="amount_actual">
                                        <TextField
                                            fullWidth
                                            label="Amount ($)"
                                            name="amount_actual"
                                            type="number"
                                            value={formData.amount_actual}
                                            onChange={handleInputChange}
                                            variant="outlined"
                                            margin="normal"
                                            error={!!formErrors.amount_actual}
                                            helperText={formErrors.amount_actual || ''}
                                        />
                                    </Grid>
                                )}

                                {!isCancellation && (
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth variant="outlined" margin="normal">
                                            <InputLabel>Refund Term</InputLabel>
                                            <Select
                                                name="refund_term"
                                                value={formData.refund_term}
                                                onChange={handleInputChange}
                                                label="Refund Term"
                                            >
                                                <MenuItem value=""><em>None</em></MenuItem>
                                                <MenuItem value="Per Pax">Per Pax</MenuItem>
                                                <MenuItem value="Per Qty">Per Qty</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )}

                                {!isCancellation && (
                                    <Grid item xs={12}>
                                        <FormControl fullWidth variant="outlined" margin="normal">
                                            <InputLabel>Applicable For</InputLabel>
                                            <Select
                                                name="applicable_for_id"
                                                value={formData.applicable_for_id}
                                                onChange={handleInputChange}
                                                label="Applicable For"
                                                disabled={loadingAccounts}
                                            >
                                                <MenuItem value=""><em>Select an Account</em></MenuItem>
                                                {accounts.map(acc => (
                                                    <MenuItem key={acc.id} value={acc.id}>{acc.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )}
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Payment Term'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewPaymentTermModal;